import React, { useState } from 'react';
import { LuExternalLink, LuEye, LuFileText, LuPartyPopper } from 'react-icons/lu';
import { ActionMenu, SearchInput } from '../ui';
import {
  useCaseExecutions,
  useCaseOverview,
  useDebouncedUrlSearch,
  useEnvironmentReport,
  useTestHistory,
} from './reportHooks';
import { formatDate, formatDateTime, formatDuration, testKeyOf, toNumber } from './reportData';
import {
  DrawerActions,
  HBarList,
  KpiStrip,
  PatternBadge,
  ReportDrawer,
  ReportEmptyState,
  ReportErrorState,
  ReportKpi,
  ReportSection,
  ReportSkeleton,
  ReportTable,
  RunStrip,
  StatusPill,
  TestCaseDetails,
} from './ReportUI';

/** Row shape for the drawer, built from a test's most recent failed run. */
const lastFailureRow = (t) => {
  const run = t.runs.find((r) => r.status === 'Failed') || t.runs[0] || {};
  return {
    test_case_id: t.testCaseId,
    test_case_name: t.name,
    suite_name: run.suite || t.suite,
    test_plan_name: run.plan || t.plan,
    environment_name: run.environment,
    status: run.status,
    duration: run.duration,
    executed_at: run.executedAt,
    execution_id: run.executionId,
  };
};

export default function FailureReport({ ctx, params, update, nav }) {
  const totals = useCaseExecutions(ctx, { page: 1, pageSize: 1, search: '', status: 'Failed' });
  const failures = useCaseExecutions(ctx, {
    page: params.f_page,
    pageSize: params.f_size,
    search: params.f_q,
    status: 'Failed',
  });
  const history = useTestHistory(ctx);
  const caseOverview = useCaseOverview(ctx);
  const envs = useEnvironmentReport(ctx);
  const [query, setQuery] = useDebouncedUrlSearch(params.f_q, (v) => update({ f_q: v }));
  const [drawerRow, setDrawerRow] = useState(null);

  const h = history.data;
  const totalFailed = totals.data?.total ?? null;
  const historyFor = (row) => h?.map?.get(testKeyOf(row));

  if (totals.isError && totalFailed == null) {
    return <ReportErrorState onRetry={() => totals.refetch()} />;
  }

  if (totalFailed === 0) {
    return (
      <ReportEmptyState
        icon={<LuPartyPopper size={20} />}
        title="No failures 🎉"
        description={
          h?.recovered
            ? `All tests passed in this period, and ${h.recovered} previously failing test${h.recovered !== 1 ? 's' : ''} recovered.`
            : 'All executed tests passed in this period.'
        }
        action={
          <button type="button" className="rt-btn rt-btn--secondary rt-btn--sm" onClick={() => nav.toReport('summary')}>
            Back to Summary
          </button>
        }
      />
    );
  }

  const suiteItems = (caseOverview.data?.chart || [])
    .filter((s) => toNumber(s.failed) > 0)
    .sort((a, b) => toNumber(b.failed) - toNumber(a.failed))
    .slice(0, 8)
    .map((s, i) => {
      const total = toNumber(s.passed) + toNumber(s.failed) + toNumber(s.pending);
      return {
        key: `${s.suite_name || 'suite'}-${i}`,
        label: s.suite_name || '—',
        value: toNumber(s.failed),
        sub: total ? `${Math.round((toNumber(s.failed) / total) * 100)}% of ${total} runs` : undefined,
      };
    });

  const envItems = (envs.data || [])
    .filter((e) => e.failed > 0)
    .sort((a, b) => b.failed - a.failed)
    .slice(0, 8)
    .map((e) => ({
      key: e.id ?? e.name,
      label: e.name,
      value: e.failed,
      sub: e.totalExecuted ? `${Math.round(e.passRate)}% pass rate` : undefined,
    }));

  const columns = [
    {
      key: 'pattern',
      header: 'Pattern',
      sortable: true,
      sortValue: (r) => historyFor(r)?.pattern,
      render: (r) => (history.isLoading ? <span className="rt-skeleton" style={{ width: 56, height: 16 }} /> : <PatternBadge pattern={historyFor(r)?.pattern} />),
    },
    { key: 'test_case_name', header: 'Test case', sortable: true, render: (r) => <span className="rpt-cell-title">{r.test_case_name || '—'}</span> },
    { key: 'suite_name', header: 'Suite', sortable: true, render: (r) => r.suite_name || '—' },
    { key: 'test_plan_name', header: 'Test plan', render: (r) => r.test_plan_name || <span className="rpt-tag">Individual run</span> },
    { key: 'environment_name', header: 'Environment', sortable: true, render: (r) => r.environment_name || '—' },
    {
      key: 'duration',
      header: 'Duration',
      align: 'right',
      sortable: true,
      sortValue: (r) => toNumber(r.duration, null),
      render: (r) => <span className="rpt-mono">{formatDuration(r.duration)}</span>,
    },
    { key: 'executed_at', header: 'Failed at', sortable: true, render: (r) => formatDateTime(r.executed_at) },
    {
      key: 'occurrences',
      header: 'Occurrences',
      align: 'right',
      sortable: true,
      sortValue: (r) => historyFor(r)?.failureCount ?? null,
      render: (r) => {
        const t = historyFor(r);
        if (!t) return <span className="rpt-muted">—</span>;
        return (
          <span title={`Failed ${t.failureCount} of ${t.runCount} runs in this period`}>
            <b className={t.failureCount > 1 ? 'rpt-text--danger' : undefined}>{t.failureCount}×</b>
            <span className="rpt-muted"> / {t.runCount}</span>
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: <span className="rpt-sr-only">Actions</span>,
      align: 'right',
      render: (r) => (
        <span onClick={(e) => e.stopPropagation()} role="presentation">
          <ActionMenu
            label={`Actions for ${r.test_case_name}`}
            items={[
              { id: 'view', label: 'View failure', icon: <LuEye size={14} />, onClick: () => setDrawerRow(r) },
              r.test_case_id != null && {
                id: 'test',
                label: 'Open test case',
                icon: <LuFileText size={14} />,
                onClick: () => nav.openTestCase(r.test_case_id),
              },
              r.execution_id != null && {
                id: 'exec',
                label: 'Open execution',
                icon: <LuExternalLink size={14} />,
                onClick: () => nav.openCaseExecution(r),
              },
            ].filter(Boolean)}
          />
        </span>
      ),
    },
  ];

  const drawerHistory = drawerRow ? historyFor(drawerRow) : null;

  return (
    <div className="rpt-stack">
      <div className="rpt-failure-hero">
        <div>
          <span className="rpt-failure-hero__count">
            {totals.isLoading ? <span className="rt-skeleton" style={{ width: 80, height: 32 }} /> : (totalFailed ?? 0).toLocaleString()}
          </span>
          <span className="rpt-failure-hero__label">failed execution{totalFailed !== 1 ? 's' : ''} in this period</span>
        </div>
        {h && (
          <p className="rpt-failure-hero__summary">
            {h.uniqueFailed} test{h.uniqueFailed !== 1 ? 's' : ''} failed at least once —{' '}
            <b className="rpt-text--danger">{h.newFailures} new</b>,{' '}
            <b className="rpt-text--warning">{h.recurring} recurring</b>,{' '}
            <b className="rpt-text--success">{h.recovered} recovered</b>.
          </p>
        )}
      </div>

      {history.isLoading ? (
        <ReportSkeleton variant="kpis" rows={4} />
      ) : (
        <KpiStrip label="Failure summary">
          <ReportKpi label="Unique failed tests" value={h?.uniqueFailed ?? '—'} tone="danger" />
          <ReportKpi label="New failures" value={h?.newFailures ?? '—'} hint="first failure in period" />
          <ReportKpi label="Recurring" value={h?.recurring ?? '—'} tone={h?.recurring ? 'warning' : undefined} hint="failed more than once" />
          <ReportKpi label="Recovered" value={h?.recovered ?? '—'} tone={h?.recovered ? 'success' : undefined} hint="now passing" />
        </KpiStrip>
      )}
      {h?.truncated && (
        <p className="rpt-caption">
          Patterns are based on the latest {h.rows.length.toLocaleString()} of {h.total.toLocaleString()} executions in this period.
        </p>
      )}

      <div className="rpt-grid rpt-grid--2">
        <ReportSection title="Failures by suite" subtitle="Where failures concentrate">
          {caseOverview.isLoading && <ReportSkeleton variant="list" rows={4} />}
          {caseOverview.isError && <ReportErrorState compact onRetry={() => caseOverview.refetch()} />}
          {caseOverview.data && <HBarList items={suiteItems} ariaLabel="Failed executions by suite" emptyText="No suite-level failures recorded." />}
        </ReportSection>
        <ReportSection title="Failures by environment" subtitle="Is the problem environment-specific?">
          {envs.isLoading && <ReportSkeleton variant="list" rows={4} />}
          {envs.isError && <ReportErrorState compact onRetry={() => envs.refetch()} />}
          {envs.data && <HBarList items={envItems} ariaLabel="Failed executions by environment" emptyText="No environment-level failures recorded." />}
        </ReportSection>
      </div>

      <div className="rpt-grid rpt-grid--2">
        <ReportSection title="Investigate first" subtitle="Most frequent failures, then most recent">
          {history.isLoading && <ReportSkeleton variant="list" rows={5} />}
          {history.isError && <ReportErrorState compact onRetry={() => history.refetch()} />}
          {h && !h.investigateFirst.length && <p className="rpt-empty-inline">No failing tests found.</p>}
          {h && h.investigateFirst.length > 0 && (
            <ol className="rpt-ranklist">
              {h.investigateFirst.map((t, i) => (
                <li key={t.key}>
                  <button type="button" className="rpt-ranklist__row" onClick={() => setDrawerRow(lastFailureRow(t))}>
                    <span className="rpt-ranklist__rank">{i + 1}</span>
                    <span className="rpt-ranklist__main">
                      <span className="rpt-ranklist__title">{t.name}</span>
                      <span className="rpt-ranklist__sub">
                        {t.suite || '—'}
                        {t.environment ? ` · ${t.environment}` : ''} · last failed {formatDate(t.lastFailedAt)}
                      </span>
                    </span>
                    <PatternBadge pattern={t.pattern} />
                    <span className="rpt-ranklist__value">
                      <b className="rpt-text--danger">{t.failureCount}×</b>
                    </span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </ReportSection>

        <ReportSection title="Persistent failures" subtitle="Tests that failed repeatedly in this period">
          {history.isLoading && <ReportSkeleton variant="list" rows={5} />}
          {h && !h.persistent.length && (
            <p className="rpt-empty-inline">No test failed more than once in this period.</p>
          )}
          {h && h.persistent.length > 0 && (
            <ul className="rpt-persistent">
              {h.persistent.map((t) => (
                <li key={t.key}>
                  <button type="button" className="rpt-persistent__row" onClick={() => setDrawerRow(lastFailureRow(t))}>
                    <span className="rpt-persistent__main">
                      <span className="rpt-ranklist__title">{t.name}</span>
                      <span className="rpt-ranklist__sub">
                        Failed <b>{t.failureCount}</b> of {t.runCount} runs
                        {t.failStreak > 1 ? ` · ${t.failStreak} in a row` : ''}
                        {t.latestStatus === 'Passed' ? ' · latest run passed' : ''}
                      </span>
                    </span>
                    <RunStrip runs={t.runs} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </ReportSection>
      </div>

      <ReportSection
        title="Failed executions"
        subtitle="Every failed run in this period — open one for details and history"
        actions={<SearchInput value={query} onChange={setQuery} placeholder="Search failed tests…" className="rpt-search" />}
        flush
      >
        <ReportTable
          caption="Failed test executions"
          columns={columns}
          rows={failures.data?.rows || []}
          rowKey={(r, i) => r.execution_id ?? `${r.test_case_id}-${r.executed_at}-${i}`}
          loading={failures.isLoading}
          fetching={failures.isFetching}
          error={failures.isError}
          onRetry={() => failures.refetch()}
          onRowClick={setDrawerRow}
          rowLabel={(r) => `View failure of ${r.test_case_name}`}
          sort={params.f_sort}
          onSortChange={(s) => update({ f_sort: s })}
          emptyTitle={params.f_q ? 'No failed tests match your search' : 'No failed executions'}
          pagination={{
            page: params.f_page,
            pageSize: params.f_size,
            total: failures.data?.total || 0,
            onPageChange: (p) => update({ f_page: p }, { push: true }),
            onPageSizeChange: (n) => update({ f_size: n }),
            pageSizeOptions: [10, 20, 50, 100],
          }}
        />
      </ReportSection>

      <ReportDrawer
        open={Boolean(drawerRow)}
        onClose={() => setDrawerRow(null)}
        eyebrow="Failed test"
        title={drawerRow?.test_case_name || 'Test case'}
        badge={
          drawerRow && (
            <>
              <StatusPill status={drawerRow.status} />
              {drawerHistory && <PatternBadge pattern={drawerHistory.pattern} />}
            </>
          )
        }
        footer={
          drawerRow && (
            <DrawerActions
              actions={[
                drawerRow.test_case_id != null && { label: 'Open test case', onClick: () => nav.openTestCase(drawerRow.test_case_id) },
                drawerRow.execution_id != null && { label: 'Open execution', primary: true, onClick: () => nav.openCaseExecution(drawerRow) },
              ]}
            />
          )
        }
      >
        {drawerRow && (
          <TestCaseDetails
            row={drawerRow}
            history={drawerHistory}
            historyLoading={history.isLoading}
            historyTruncated={h?.truncated}
          />
        )}
      </ReportDrawer>
    </div>
  );
}
